import { useMemo, useState } from "react";
import { TextInput } from "risa-oasis-ui_v2";
import ChevronDownSm from "../../../../../svg/chevron-down-sm";
import { Delete } from "../../../../../svg/delete";
import {
  BiosimilarFamily,
  DoseConversionRow,
  ReverseConversionRow,
} from "../types";
import {
  fromConversionRows,
  fromReverseConversionRows,
  toConversionRows,
  toReverseConversionRows,
} from "../utils/biosimilarConfigHelpers";
import CollapsibleSection from "./CollapsibleSection";

/**
 * Props for the dose conversion editor inside a family card.
 */
interface DoseConversionsSectionProps {
  /** Family being edited. */
  family: BiosimilarFamily;
  /** Applies family updates from conversion edits. */
  onUpdate: (patch: Partial<BiosimilarFamily>) => void;
}

/**
 * Collapsible dose conversion and reverse conversion editor for one family.
 *
 * @param props - Family data and update handler
 * @returns Conversion section for a family card
 */
const DoseConversionsSection = ({
  family,
  onUpdate,
}: DoseConversionsSectionProps) => {
  const [expanded, setExpanded] = useState(true);
  const conversionRows = useMemo(
    () => toConversionRows(family.dose_conversions),
    [family.dose_conversions],
  );
  const reverseRows = useMemo(
    () => toReverseConversionRows(family.reverse_conversions),
    [family.reverse_conversions],
  );

  const applyConversionRows = (
    rows: DoseConversionRow[],
    reverse: ReverseConversionRow[],
  ) => {
    const hasConversions = rows.some((row) => row.fromRegimen.trim());
    onUpdate({
      dose_conversions: fromConversionRows(rows),
      reverse_conversions: fromReverseConversionRows(reverse),
      type: hasConversions ? "dose_converted" : family.type,
    });
  };

  const updateConversionRow = (
    index: number,
    patch: Partial<DoseConversionRow>,
  ) => {
    const nextRows = conversionRows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, ...patch } : row,
    );
    applyConversionRows(nextRows, reverseRows);
  };

  const removeConversionRow = (index: number) => {
    const nextRows = conversionRows.filter((_, rowIndex) => rowIndex !== index);
    applyConversionRows(nextRows, reverseRows);
  };

  const addConversionRow = () => {
    applyConversionRows(
      [
        ...conversionRows,
        { fromRegimen: "", dose: "", package_requirement: "" },
      ],
      reverseRows,
    );
  };

  const updateReverseRow = (
    index: number,
    patch: Partial<ReverseConversionRow>,
  ) => {
    const nextRows = reverseRows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, ...patch } : row,
    );
    applyConversionRows(conversionRows, nextRows);
  };

  const removeReverseRow = (index: number) => {
    const nextRows = reverseRows.filter((_, rowIndex) => rowIndex !== index);
    applyConversionRows(conversionRows, nextRows);
  };

  const addReverseRow = () => {
    applyConversionRows(conversionRows, [
      ...reverseRows,
      { fromRegimen: "", toRegimen: "" },
    ]);
  };

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        className="flex w-fit flex-row items-center gap-2 text-left text-tiny font-semibold uppercase text-primaryGray-7"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <span
          className={`flex transition-transform duration-300 ease-in-out ${
            expanded ? "rotate-0" : "-rotate-90"
          }`}
        >
          <ChevronDownSm />
        </span>
        Dose Conversions
      </button>

      <CollapsibleSection expanded={expanded}>
        <div className="flex flex-col gap-3 pl-4">
          {conversionRows.length === 0 ? (
            <div className="text-tiny text-primaryGray-7">
              No dose conversions — adding one will mark this family as dose
              converted automatically.
            </div>
          ) : null}

          {conversionRows.map((row, index) => (
            <div
              key={`conversion-${index}`}
              className="grid grid-cols-1 items-end gap-2 rounded border border-primaryGray-16 p-3 md:grid-cols-[1fr_1fr_1fr_auto]"
            >
              <TextInput
                id={`conversion-from-${index}`}
                label="From regimen"
                placeholder="100 mcg Q14D"
                defaultValue={row.fromRegimen}
                onChange={(event) =>
                  updateConversionRow(index, { fromRegimen: event.value })
                }
              />
              <TextInput
                id={`conversion-dose-${index}`}
                label="Dose"
                placeholder="20,000 units Q14D"
                defaultValue={row.dose}
                onChange={(event) =>
                  updateConversionRow(index, { dose: event.value })
                }
              />
              <TextInput
                id={`conversion-package-${index}`}
                label="Package requirement"
                placeholder="2 x 20,000"
                defaultValue={row.package_requirement}
                onChange={(event) =>
                  updateConversionRow(index, {
                    package_requirement: event.value,
                  })
                }
              />
              <button
                type="button"
                className="mb-1 flex h-8 w-8 items-center justify-center"
                onClick={() => removeConversionRow(index)}
                aria-label="Remove conversion"
              >
                <Delete height={14} width={14} />
              </button>
            </div>
          ))}

          <button
            type="button"
            className="text-small font-semibold text-blue-600"
            onClick={addConversionRow}
          >
            + Add conversion
          </button>

          {reverseRows.length > 0 || family.type === "dose_converted" ? (
            <div className="flex flex-col gap-2 border-t border-primaryGray-16 pt-3">
              <div className="text-tiny font-semibold text-primaryGray-7">
                Reverse conversions
              </div>
              {reverseRows.map((row, index) => (
                <div
                  key={`reverse-${index}`}
                  className="grid grid-cols-1 items-end gap-2 md:grid-cols-[1fr_1fr_auto]"
                >
                  <TextInput
                    id={`reverse-from-${index}`}
                    label="Units regimen"
                    defaultValue={row.fromRegimen}
                    onChange={(event) =>
                      updateReverseRow(index, { fromRegimen: event.value })
                    }
                  />
                  <TextInput
                    id={`reverse-to-${index}`}
                    label="Mcg regimen"
                    defaultValue={row.toRegimen}
                    onChange={(event) =>
                      updateReverseRow(index, { toRegimen: event.value })
                    }
                  />
                  <button
                    type="button"
                    className="mb-1 flex h-8 w-8 items-center justify-center"
                    onClick={() => removeReverseRow(index)}
                    aria-label="Remove reverse conversion"
                  >
                    <Delete height={14} width={14} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="text-small font-semibold text-blue-600"
                onClick={addReverseRow}
              >
                + Add reverse conversion
              </button>
            </div>
          ) : null}
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default DoseConversionsSection;
